// Assessment Criteria help copy — surfaced via the (?) popover next to each row.
// Keep this UK-marking flavoured so bands align with performanceBand() thresholds:
//   85–100 Excellent · 70–84 Good · 60–69 Satisfactory · 50–59 Needs Improvement · <50 Significant Improvement Required

export const CRITERIA_HELP = {
  "Assignment Fit": {
    title: "Assignment Fit",
    what:
      "How closely your submission addresses the specific brief, question and learning outcomes provided — not just how well-written it is in general.",
    bands: [
      { label: "Excellent (85–100)", detail: "Every part of the brief is answered directly, with precise interpretation and sustained relevance throughout." },
      { label: "Good (70–84)", detail: "The brief is answered thoroughly with only minor drift; some sections could be tied back more explicitly." },
      { label: "Satisfactory (60–69)", detail: "Main question addressed but supporting requirements (learning outcomes, sub-questions) are partially covered." },
      { label: "Needs Improvement (50–59)", detail: "Brief only loosely addressed; large parts of the response go off-topic or miss key requirements." },
      { label: "Significant Improvement Required (<50)", detail: "The brief and outcomes are largely unanswered." },
    ],
    next: "Re-read the brief line by line and add explicit signposts linking each paragraph back to the specific question or outcome it addresses.",
  },
  "Critical Analysis": {
    title: "Critical Analysis",
    what:
      "Depth of thinking — comparing, evaluating and weighing evidence rather than describing or summarising sources.",
    bands: [
      { label: "Excellent (85–100)", detail: "Sustained critical evaluation, original synthesis of sources, and confident argumentation." },
      { label: "Good (70–84)", detail: "Clear analytical thread with regular evaluation, though some points remain descriptive." },
      { label: "Satisfactory (60–69)", detail: "Some analysis but frequently descriptive; sources are cited but not compared." },
      { label: "Needs Improvement (50–59)", detail: "Mostly descriptive, few evaluative statements, arguments not clearly built." },
      { label: "Significant Improvement Required (<50)", detail: "Almost entirely descriptive with no critical engagement." },
    ],
    next: "Add sentences that begin with phrases like ‘however’, ‘by contrast’, ‘this suggests that’ — showing you are weighing evidence rather than reporting it.",
  },
  "Academic Writing": {
    title: "Academic Writing",
    what:
      "Grammar, tone, vocabulary, sentence variety, flow, objectivity and readability. A composite score based on multiple writing-quality signals.",
    bands: [
      { label: "Excellent (85–100)", detail: "Formal, precise, varied sentence structure, error-free, distinctly academic voice." },
      { label: "Good (70–84)", detail: "Clear and consistent academic tone with only occasional lapses or awkward phrasings." },
      { label: "Satisfactory (60–69)", detail: "Generally readable but noticeable informalities, repetitiveness or small grammar issues." },
      { label: "Needs Improvement (50–59)", detail: "Frequent lapses in tone, repetitive sentences, or grammar/spelling errors that distract the reader." },
      { label: "Significant Improvement Required (<50)", detail: "Writing quality impedes understanding; substantial editing required." },
    ],
    next: "Read your work aloud, replace informal phrases, vary sentence openings, and remove first-person pronouns unless the brief invites reflection.",
  },
  "Referencing": {
    title: "Referencing",
    what:
      "Accuracy, completeness and consistency of your citations and reference list against the required style (APA, Harvard, MLA, Vancouver, etc.).",
    bands: [
      { label: "Excellent (85–100)", detail: "Complete, correctly formatted reference list; in-text citations match; consistent style throughout." },
      { label: "Good (70–84)", detail: "Referencing is largely correct with minor formatting slips (missing italics, punctuation, page numbers)." },
      { label: "Satisfactory (60–69)", detail: "Most citations present but style is inconsistent, or a few entries are incomplete." },
      { label: "Needs Improvement (50–59)", detail: "Many citations missing, entries incomplete, or a mix of two styles used." },
      { label: "Significant Improvement Required (<50)", detail: "No reference list detected or almost no in-text citations found." },
    ],
    next: "Cross-check every in-text citation against the reference list, standardise on one style, and add any missing DOI/URL/page numbers.",
  },
  "Writing Authenticity": {
    title: "Writing Authenticity",
    what:
      "How personal, specific and grounded your writing sounds — the opposite of generic, formulaic or overly broad academic phrasing.",
    bands: [
      { label: "Excellent (85–100)", detail: "Distinct voice, specific evidence, and personalised arguments throughout." },
      { label: "Good (70–84)", detail: "Mostly personal writing with a few generic passages that could be strengthened." },
      { label: "Satisfactory (60–69)", detail: "Several passages read as generic or formulaic; specific evidence is thin in places." },
      { label: "Needs Improvement (50–59)", detail: "Many passages feel templated with vague qualifiers and unsupported claims." },
      { label: "Significant Improvement Required (<50)", detail: "Substantial review recommended — the writing lacks specificity and personal engagement." },
    ],
    next: "Replace broad claims with specific examples, data points or case references. Cut generic phrases and add your own critical interpretation.",
  },
  "Structure": {
    title: "Structure",
    what:
      "Organisation, flow and section sequencing — how clearly your argument builds from introduction to conclusion. Does NOT judge whether content is present.",
    bands: [
      { label: "Excellent (85–100)", detail: "Clear, purposeful structure with strong signposting; each section builds logically on the last." },
      { label: "Good (70–84)", detail: "Well-organised with a few sequencing issues or transitions that could be smoother." },
      { label: "Satisfactory (60–69)", detail: "Structure is discernible but some sections are out of order or unclear in purpose." },
      { label: "Needs Improvement (50–59)", detail: "Structure is inconsistent, paragraphs drift, and section boundaries are unclear." },
      { label: "Significant Improvement Required (<50)", detail: "No clear structure — the reader struggles to follow the argument." },
    ],
    next: "Add clear section headings, use topic sentences to open each paragraph, and add transition sentences that link one idea to the next.",
  },
};
